import { http } from './index'
import axios from 'axios'

/**
 * This cancel token lets you cancel request on demand
 * @type {CancelTokenStatic|*}
 */
const CancelToken = axios.CancelToken
const cancelations = []

export class ApiManager {
  /**
   * Get offer data by Id.
   * API GET Method
   * @param {string} offerId
   */
  static requestOfferData = offerId => {
    return http.get(`/api/offers/${offerId}`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'requestOfferData' })
      }),
    })
  }

  /**
   * Returns offer validationStatus.
   * API GET Method
   * @param {string} offerId
   */
  static requestValidationStatus = offerId => {
    return http.get(`/api/offers/${offerId}/validation-status`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'requestValidationStatus' })
      }),
    })
  }

  /**
   * Update in db a previously created offer
   * API PUT Method
   * @param {string} offerId
   * @param {Object} data - offer data
   */
  static updateOffer = (offerId, data) => {
    return http.put(`/api/offers/${offerId}`, data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'updateOffer' })
      }),
    })
  }

  /**
   * Send offer data to CRM & increase offer version
   * API POST Method
   * @param { string } offerId
   */
  static updateAndSendOffer = (offerId, data) => {
    return http.post(`/api/crm/offers/${offerId}/`, data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'updateAndSendOffer' })
      }),
    })
  }

  /**
   * Create new offer
   * API POST Method
   * @param { Object } data
   * @param { Object } data.client
   * @param { Object } data.comercialAgent
   * @param { string } data.currencyISO
   * @param { string } data.offerExternalId
   * @param { string } data.potentialShipments
   * @param { number } data.rateModelId
   */
  static createOffer = data => {
    return http.post('/api/offers/', data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'createOffer' })
      }),
    })
  }

  /**
   * Reset offer to created state
   * API POST Method
   * @param { Object } data
   * @param { Object } data.client
   * @param { Object } data.comercialAgent
   * @param { string } data.currencyISO
   * @param { string } data.offerExternalId
   * @param { string } data.potentialShipments
   * @param { number } data.rateModelId
   * @param { string } data.offerId
   */
  static resetOffer = data => {
    return http.post('/api/offers/?resetFlag=true', data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'resetOffer' })
      }),
    })
  }

  /**
   * Send contract - set offer status to 'SIGNING'
   * API POST Method
   * @param { string } offerId
   * @param { Object } data - contract data
   */
  static sendContractToSign = (offerId, data) => {
    return http.post(`/api/offers/${offerId}/contract/sign`, data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'sendContract' })
      }),
    })
  }

  /**
   * Set offer status to 'SIGNED'
   * API POST Method
   * @param { string } offerId
   * @param { Object } data - contract data
   */
  static manualSign = (offerId, data) => {
    return http.post(`/api/offers/${offerId}/contract/manual-sign`, data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'manualSign' })
      }),
    })
  }

  /**
   * Cancellation of the signature process (possible only before signature concluded - offer status 'SIGNED')
   * API POST Method
   * @param { string } offerId
   */
  static cancelSign = offerId => {
    return http.post(`/api/offers/${offerId}/contract/cancel`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'cancelSign' })
      }),
    })
  }

  /**
   * Import a new tariff model from the admin dashboard
   * API POST Method
   * @param { Object } data
   */
  static uploadModel = data => {
    return http.post(`/api/rate-model/import`, data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'uploadModel' })
      }),
    })
  }

  /**
   * Request contract status
   * API PUT Method
   * @param { string } offerId
   * @returns { Object } contract data (contractId, documentId, created_at, status, events)
   */
  static requestContractStatus = offerId => {
    return http.put(`/api/offers/${offerId}/contract/`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'requestContractStatus' })
      }),
    })
  }

  /**
   * Get the contract from db
   * API GET Method
   * @param { string } offerExternalId
   */
  static requestContract = offerId => {
    return http.get(
      `/api/offers/${offerId}/contract`,
      { responseType: 'blob' },
      {
        cancelToken: new CancelToken(function executor(c) {
          cancelations.push({ token: c, service: 'requestContract' })
        }),
      },
    )
  }

  /**
   * Get offer list to show in the admin dashboard
   * API GET Method
   */
  static requestOfferList = () => {
    return http.get(`/api/admin/offers/`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'requestOfferList' })
      }),
    })
  }

  /**
   * Get information about the available tariff models
   * API GET Method
   * @param {string|null} organization - Optional organization ID
   */
  static requestModels(organization = null) {
    const params = new URLSearchParams();
    params.append("active", "true");

    if (organization) {
      params.append("organization", organization);
    }

    return http.get(`/api/rate-models?${params.toString()}`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: "requestModels" });
      }),
    });
  }


  /**
   * Get organization information based on the rate model of the offer
   * API GET Method
   */
  static requestOrganization = (rateModelId) => {
    return http.get(`/api/rate-models/${rateModelId}`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'requestOrganization' })
      }),
    })
  }

  /**
   * Get information about a specific tariff model
   * API GET Method
   * @param { string } rateModelId
   * @return tariff model general information
   */
  static requestModel = rateModelId => {
    return http.get(`/api/rate-models/${rateModelId}`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'requestModels' })
      }),
    })
  }

  /**
   * Set the tariff model for a previously created offer (rateModelId)
   * API PUT Method
   * @param { string } idUser
   * @return new offer data with with the corresponding tariff pricing info
   */
  static setRateModel = (offerId, model, data) => {
    return http.put(
      `/api/offers/${offerId}/rate-model`,
      data,
      {
        params: {
          ...(model && { model: model }),
        },
      },
      {
        cancelToken: new CancelToken(function executor(c) {
          cancelations.push({ token: c, service: 'setRateModel' })
        }),
      },
    )
  }

  /**
   * Copy data from an offer such as rate model, products prizes...
   * API PUT Method
   * @param { string } offerId - Target offerId
   * @param { string } sourceOfferId - Source offerId
   */
  static cloneOffer = (offerId, sourceOfferId) => {
    return http.put(
      `/api/offers/${offerId}/copy`,
      { sourceOfferId },
      {
        cancelToken: new CancelToken(function executor(c) {
          cancelations.push({ token: c, service: 'cloneOffer' })
        }),
      },
    )
  }

  /**
   * Get the available translations
   * API GET Method
   */
  static requestTranslations = options => {
    const queryParams = new URLSearchParams(options).toString()

    let url = '/api/admin/translations'

    if (queryParams.length) {
      url += `?${queryParams}`
    }

    return http.get(url, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'requestTranslations' })
      }),
    })
  }

  /**
   * Add a new translation
   * API POST Method
   * @param { Object } data
   * @param { String } data.key
   * @param { String } data.value
   */
  static createTranslation = data => {
    return http.post('/api/admin/translations', data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'createTranslation' })
      }),
    })
  }

  /**
   * Update a translation
   * API PUT Method
   * @param { String } translationKey
   * @param { Object } data
   * @param { String } data.value
   */
  static updateTranslation = (translationKey, data) => {
    return http.put(`/api/admin/translations/${translationKey}`, data, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'updateTranslation' })
      }),
    })
  }

  /**
   * Delete a translation
   * API DELETE Method
   * @param { String } translationKey
   */
  static deleteTranslation = translationKey => {
    return http.delete(`/api/admin/translations/${translationKey}`, {
      cancelToken: new CancelToken(function executor(c) {
        cancelations.push({ token: c, service: 'deleteTranslation' })
      }),
    })
  }
}
